import { BsdsInput } from '@bsds-lib-component/react';
import React, { type FormEvent } from 'react';

import { Button, Dropdown, Visibility, ZipCodeInput } from '../..';
import type { PersonFormBaseValues, PersonFormSection, PersonFormSectionLabels } from '../../../types/vida/cadastralMaintenance/personFormTypes';
import {
  BAIRRO_MAX_LENGTH,
  CIDADE_MAX_LENGTH,
  LOGRADOURO_MAX_LENGTH,
  formatCapitalForDisplay,
  formatSalaryForDisplay,
  getInputValue,
  normalizeCapitalFormValue,
  normalizeSalaryFormValue,
} from '../../../utils/insuredFormUtils';
import { formatDateForDateInput } from '../../../utils/inclusionProponentUtil';
import { BRAZILIAN_UF_OPTIONS } from '../../../utils/proponentFormUtils';
import { isZipCodeComplete } from '../../../utils/zipCodeAddressUtils';

type PersonFormSectionNavProps = {
  labels: PersonFormSectionLabels;
  activeSection: PersonFormSection;
  onSectionChange: (section: PersonFormSection) => void;
  extraNavButtons?: React.ReactNode;
  highlightActiveSection?: boolean;
};

export const PersonFormSectionNav: React.FC<PersonFormSectionNavProps> = ({
  labels,
  activeSection,
  onSectionChange,
  extraNavButtons,
  highlightActiveSection = true,
}) => (
  <div className="bsds-display-flex bsds-align-items-center flex-wrap gap-4 bsds-mb-16 pb-3 bsds-mt-24">
    <Button
      type="button"
      color="primary"
      fill={highlightActiveSection && activeSection === 'personData' ? 'solid' : 'outline'}
      onClick={() => onSectionChange('personData')}
      aria-label={labels.personDataAriaLabel}
      aria-pressed={highlightActiveSection && activeSection === 'personData'}
    >
      {labels.personDataButton}
    </Button>
    <Button
      type="button"
      color="primary"
      fill={highlightActiveSection && activeSection === 'personAddress' ? 'solid' : 'outline'}
      onClick={() => onSectionChange('personAddress')}
      aria-label={labels.personAddressAriaLabel}
      aria-pressed={highlightActiveSection && activeSection === 'personAddress'}
    >
      {labels.personAddressButton}
    </Button>
    <Button
      type="button"
      color="primary"
      fill={highlightActiveSection && activeSection === 'insuranceInfo' ? 'solid' : 'outline'}
      onClick={() => onSectionChange('insuranceInfo')}
      aria-label={labels.insuranceInfoAriaLabel}
      aria-pressed={highlightActiveSection && activeSection === 'insuranceInfo'}
    >
      {labels.insuranceInfoButton}
    </Button>
    {extraNavButtons}
  </div>
);

type PersonFormDateInputProps = {
  label: string;
  value: string;
  onInput: (event: React.FormEvent<HTMLBsdsInputElement>) => void;
  disabled?: boolean;
  ariaLabel: string;
};

export const PersonFormDateInput: React.FC<PersonFormDateInputProps> = ({
  label,
  value,
  onInput,
  disabled = false,
  ariaLabel,
}) => (
  <BsdsInput
    label={label}
    value={formatDateForDateInput(value)}
    onInput={onInput}
    type="date"
    color="primary"
    platform="web"
    className="bsds-width-100"
    required
    disabled={disabled}
    clearInput={false}
    placeholder="yyyy-MM-dd"
    aria-label={ariaLabel}
    aria-required
  />
);

type PersonFormAddressFieldsProps<T extends PersonFormBaseValues> = {
  isActive: boolean;
  values: T;
  ufDropdownId: string;
  inputDisabled: boolean;
  onInputChange: (field: keyof T) => (e: React.FormEvent<HTMLBsdsInputElement>) => void;
  onCepChange: (value: string) => void;
  onDropdownChange: (field: keyof T) => (value: string) => void;
  onZipCodeSearch?: () => void;
  isZipCodeLookupLoading?: boolean;
  zipCodeLookupError?: string | null;
  extraFields?: React.ReactNode;
};

export function PersonFormAddressFields<T extends PersonFormBaseValues>({
  isActive,
  values,
  ufDropdownId,
  inputDisabled,
  onInputChange,
  onCepChange,
  onDropdownChange,
  onZipCodeSearch,
  isZipCodeLookupLoading = false,
  zipCodeLookupError = null,
  extraFields,
}: PersonFormAddressFieldsProps<T>) {
  const showZipCodeSearchButton = isZipCodeComplete(values.cep);
  /** Logradouro, city and uf are filled only by zip code lookup. */
  const isZipLookupOnlyFieldDisabled = true;

  return (
    <Visibility isActive={isActive} animated animationDuration={300}>
      <div className="bsds-mt-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-mt-16">
          <div className="md:col-span-1">
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <ZipCodeInput
                  label="CEP *"
                  value={values.cep}
                  onChange={onCepChange}
                  placeholder="00000-000"
                  required
                  disabled={inputDisabled || isZipCodeLookupLoading}
                  aria-label="CEP"
                />
              </div>
              {showZipCodeSearchButton && (
                <Button
                  type="button"
                  color="primary"
                  fill="outline"
                  className="shrink-0 px-3 py-2 text-sm leading-tight"
                  loading={isZipCodeLookupLoading}
                  disabled={inputDisabled || isZipCodeLookupLoading}
                  onClick={() => {
                    void onZipCodeSearch?.();
                  }}
                  aria-label="Buscar endereço pelo CEP"
                >
                  Buscar
                </Button>
              )}
            </div>
            {zipCodeLookupError && (
              <p className="text-sm text-red-600 bsds-mt-4" role="alert">
                {zipCodeLookupError}
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <BsdsInput
              label="Logradouro *"
              value={values.logradouro}
              onInput={onInputChange('logradouro')}
              type="text"
              platform="web"
              required
              disabled={isZipLookupOnlyFieldDisabled}
              clearInput={false}
              maxlength={LOGRADOURO_MAX_LENGTH}
            />
          </div>
          {extraFields}
          <BsdsInput
            label="Bairro *"
            value={values.bairro}
            onInput={onInputChange('bairro')}
            type="text"
            platform="web"
            required
            disabled={inputDisabled}
            clearInput={false}
            maxlength={BAIRRO_MAX_LENGTH}
          />
          <BsdsInput
            label="Cidade *"
            value={values.cidade}
            onInput={onInputChange('cidade')}
            type="text"
            platform="web"
            required
            disabled={isZipLookupOnlyFieldDisabled}
            clearInput={false}
            maxlength={CIDADE_MAX_LENGTH}
          />
          <Dropdown
            id={ufDropdownId}
            label="UF *"
            placeholder="Selecione a UF"
            value={values.uf}
            onChange={onDropdownChange('uf')}
            options={BRAZILIAN_UF_OPTIONS}
            required
            disabled={isZipLookupOnlyFieldDisabled}
          />
        </div>
      </div>
    </Visibility>
  );
}

type PersonFormInsuranceFieldsProps<T extends PersonFormBaseValues> = {
  isActive: boolean;
  values: T;
  inputDisabled: boolean;
  onInputChange: (field: keyof T) => (e: React.FormEvent<HTMLBsdsInputElement>) => void;
  extraFields?: React.ReactNode;
};

export function PersonFormInsuranceFields<T extends PersonFormBaseValues>({
  isActive,
  values,
  inputDisabled,
  onInputChange,
  extraFields,
}: PersonFormInsuranceFieldsProps<T>) {
  const handleCapitalInput = (event: FormEvent<HTMLBsdsInputElement>) => {
    const normalizedValue = normalizeCapitalFormValue(getInputValue(event));
    onInputChange('capital')({
      ...event,
      detail: { value: normalizedValue },
    } as FormEvent<HTMLBsdsInputElement> & { detail: { value: string } });
  };

  const handleSalaryInput = (event: FormEvent<HTMLBsdsInputElement>) => {
    const normalizedValue = normalizeSalaryFormValue(getInputValue(event));
    onInputChange('salario')({
      ...event,
      detail: { value: normalizedValue },
    } as FormEvent<HTMLBsdsInputElement> & { detail: { value: string } });
  };

  return (
    <Visibility isActive={isActive} animated animationDuration={300}>
      <div className="bsds-mt-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
          <BsdsInput
            label="Capital *"
            value={formatCapitalForDisplay(values.capital)}
            onInput={handleCapitalInput}
            type="text"
            inputmode="numeric"
            platform="web"
            required
            disabled={inputDisabled}
            clearInput={false}
          />
          <BsdsInput
            label="Salário *"
            value={formatSalaryForDisplay(values.salario)}
            onInput={handleSalaryInput}
            type="text"
            inputmode="numeric"
            platform="web"
            required
            disabled={inputDisabled}
            clearInput={false}
          />
          {extraFields}
        </div>
      </div>
    </Visibility>
  );
}
